import { Icon } from "@mdi/react";
import React, { useCallback, useEffect, useState } from "react";

import { mdiAlertCircle, mdiDelete, mdiHelpCircle } from "@mdi/js";
import { Button, Input, Select, Switch, Tooltip } from "antd";
import _debounce from "lodash/debounce";

import { Calculation, Field, Raster } from "../../models/mapLayer";
import { RasterWithFile } from "../GeoMapLoaderModal.types";
import { FormulaInput } from "./FormulaInput";
import { LabelWrapper } from "./LabelWrapper";
import styles from "./MapRow.module.scss";

interface MapProps {
  mapTypes: Field[];
  availableVariables?: Field[];
  map: { Raster?: Raster; Calculation?: Calculation };
  onDeleteMap: () => void;
  onUseExpressionChanged?: (checked: boolean) => void;
  onExpressionChanged: (expression: string) => Promise<void>;
  onMapNameChanged: (value: string) => void;
  onChange: (o: { min?: number; max?: number; unit?: string }) => Promise<void>;
  isCircular?: boolean;
}

export const MapRow: React.FC<MapProps> = ({
  mapTypes,
  availableVariables,
  map,
  onUseExpressionChanged,
  onExpressionChanged,
  onDeleteMap,
  onMapNameChanged,
  onChange,
  isCircular
}) => {
  const [min, setMin] = useState<number>((map.Raster ?? map.Calculation)?.min ?? null);
  const [max, setMax] = useState<number>((map.Raster ?? map.Calculation)?.max ?? null);

  const [localExpression, setLocalExpression] = useState<string>(() => {
    const expr = (map.Raster ?? map.Calculation)?.transformer?.expression ?? "";
    return expr === "$" ? "" : expr;
  });
  useEffect(() => {
    const expr = (map.Raster ?? map.Calculation)?.transformer?.expression ?? "";
    setLocalExpression(expr === "$" ? "" : expr);
    setMin((map.Raster ?? map.Calculation)?.min ?? null);
    setMax((map.Raster ?? map.Calculation)?.max ?? null);
  }, [map, onMapNameChanged]);

  const debouncedOnExpression = useCallback(
    _debounce(async (val) => {
      await onExpressionChanged(val);
    }, 500),
    [onExpressionChanged]
  );
  const debouncedOnChange = useCallback(
    _debounce(async (val) => {
      await onChange(val);
    }, 500),
    [onChange]
  );
  const [userHasSelected, setUserHasSelected] = useState(false);
  const useExpression = !!map.Calculation || (map.Raster && !!map.Raster.transformer);
  const noFieldMatch =
    !userHasSelected && !!(map.Raster as RasterWithFile)?._noFieldMatch;
  return (
    <div className={styles.mapLayout}>
      <LabelWrapper
        label={map.Raster ? "Field:" : "Field"}
        labelSuffix={
          map.Raster
            ? map.Raster.filepath && (
                <Tooltip placement="topLeft" title={map.Raster.filepath}>
                  <span className={styles.labelFileName}>{map.Raster.filepath}</span>
                </Tooltip>
              )
            : null
        }>
        <Select
          showSearch
          status={noFieldMatch ? "warning" : undefined}
          value={(map.Raster ?? map.Calculation)?.name}
          onSearch={(e) => {
            onMapNameChanged(e);
          }}
          onChange={(e: string) => {
            setUserHasSelected(true);
            onMapNameChanged(e);
          }}
          options={mapTypes.map((type) => ({ label: type.name, value: type.name }))}
          className={styles.fileMapFieldSelect}
        />
        {noFieldMatch ? (
          <Tooltip title="No field match found – please verify this mapping">
            <span className={styles.warningIconContainer}>
              <Icon path={mdiAlertCircle} size={"16px"} />
            </span>
          </Tooltip>
        ) : null}
      </LabelWrapper>

      <LabelWrapper label="Units">
        <Input
          onChange={async (e) => {
            await onChange({ unit: e.target.value, ...map });
          }}
          placeholder={"Unit"}
          value={(map.Raster ?? map.Calculation)?.unit ?? ""}
        />
      </LabelWrapper>

      <LabelWrapper label="Min">
        <Input
          type={"number"}
          data-testid={"geo-map-min-input"}
          onChange={(e) => {
            const val = e.target.valueAsNumber;
            setMin(val);
            debouncedOnChange({ ...(map.Raster ?? map.Calculation), min: val });
          }}
          value={min}
          placeholder={"Min"}
        />
      </LabelWrapper>

      <LabelWrapper label="Max">
        <Input
          type="number"
          data-testid={"geo-map-max-input"}
          onChange={(e) => {
            const val = e.target.valueAsNumber;
            setMax(val);
            debouncedOnChange({ ...(map.Raster ?? map.Calculation), max: val });
          }}
          value={max}
          placeholder={"Max"}
        />
      </LabelWrapper>

      {map.Raster && (
        <LabelWrapper
          label={"Scale Values"}
          labelSuffix={
            <Switch
              size="small"
              checked={useExpression}
              onChange={(checked) => {
                onUseExpressionChanged?.(checked);
              }}
            />
          }>
          <FormulaInput
            value={localExpression}
            placeholder="e.g. $Value * 100"
            fields={[]}
            disabled={!useExpression}
            onChange={(val) => {
              setLocalExpression(val);
              debouncedOnExpression(val);
            }}
          />
        </LabelWrapper>
      )}

      {map.Calculation && (
        <LabelWrapper
          label="Formula"
          labelSuffix={
            <Tooltip title="Using the available variables, you can create custom calculated map values. As you type a variable name, matching suggestions will appear — press Enter or click a suggestion to insert it. Type math operators (+, -, *, /) and numbers directly between variables.">
              <Icon path={mdiHelpCircle} size={"14px"} />
            </Tooltip>
          }>
          <FormulaInput
            value={localExpression}
            fields={availableVariables ?? mapTypes}
            disabled={!useExpression}
            placeholder="Click to build formula..."
            externalError={
              isCircular
                ? "Circular reference: this formula creates a dependency loop"
                : undefined
            }
            onChange={(val) => {
              setLocalExpression(val);
              debouncedOnExpression(val);
            }}
          />
        </LabelWrapper>
      )}

      <Button icon={<Icon path={mdiDelete} size={1.2} />} onClick={onDeleteMap} />
    </div>
  );
};
